/**
 * Particle systems (GDD §3.4 spray, §3.7 fire, smoke, sparks, splash). Ported from the concept (L577–L626) as a
 * preallocated struct-of-arrays pool: emitting and updating allocate nothing (GDD §10.5). The update maths, colour
 * ramps and the stable order of live particles are the concept's, so a scripted run matches it exactly.
 */
import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  DynamicDrawUsage,
  NormalBlending,
  Points,
  ShaderMaterial,
  type Texture,
} from 'three';
import { lerp, sstep } from '../core/math.ts';
import type { ParticleBuffer } from '../render/frame.ts';

export type ParticleKind = 'fire' | 'smoke' | 'spark' | 'mist';
const KIND: Record<ParticleKind, number> = { fire: 0, smoke: 1, spark: 2, mist: 3 };

/** An emission. Callers reuse one spec object per call site, so emitting creates no garbage. */
export interface ParticleSpec {
  kind: ParticleKind;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  life: number;
  s0: number;
  s1: number;
  drag?: number;
  grav?: number;
  /** Peak opacity (fire defaults to 0.42 as in the concept). */
  a0?: number;
  /** Smoke tint. */
  r?: number;
  g?: number;
  b?: number;
  /** Mist brightness (default 1). */
  bright?: number;
  /** Particles stop falling at this height (splash on water). */
  floor?: number;
}

const PV =
  'attribute float size; attribute float alpha; attribute vec3 pcolor; uniform float scale; varying float vA; varying vec3 vC;' +
  'void main(){ vA = alpha; vC = pcolor; vec4 mv = modelViewMatrix * vec4(position, 1.0); gl_PointSize = size * scale / max(-mv.z, 0.5); gl_Position = projectionMatrix * mv; }';
const PF =
  'uniform sampler2D map; varying float vA; varying vec3 vC;' +
  'void main(){ vec4 t = texture2D(map, gl_PointCoord); gl_FragColor = vec4(vC * t.rgb, t.a * vA);\n#include <tonemapping_fragment>\n#include <colorspace_fragment>\n}';

// Per-particle state fields (struct of arrays).
const F = {
  x: 0,
  y: 1,
  z: 2,
  vx: 3,
  vy: 4,
  vz: 5,
  age: 6,
  life: 7,
  drag: 8,
  grav: 9,
  s0: 10,
  s1: 11,
  a0: 12,
  r: 13,
  g: 14,
  b: 15,
  bright: 16,
  floor: 17,
  kind: 18,
} as const;
const STRIDE = 19;
const ATTRIBUTES = ['position', 'pcolor', 'size', 'alpha'] as const;

export class Particles {
  readonly max: number;
  readonly points: Points<BufferGeometry, ShaderMaterial>;
  private readonly state: Float64Array;
  private readonly pos: Float32Array;
  private readonly col: Float32Array;
  private readonly siz: Float32Array;
  private readonly alp: Float32Array;
  private n = 0;

  constructor(max: number, tex: Texture | null, additive: boolean) {
    this.max = max;
    this.state = new Float64Array(max * STRIDE);
    this.pos = new Float32Array(max * 3);
    this.col = new Float32Array(max * 3);
    this.siz = new Float32Array(max);
    this.alp = new Float32Array(max);
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(this.pos, 3).setUsage(DynamicDrawUsage));
    g.setAttribute('pcolor', new BufferAttribute(this.col, 3).setUsage(DynamicDrawUsage));
    g.setAttribute('size', new BufferAttribute(this.siz, 1).setUsage(DynamicDrawUsage));
    g.setAttribute('alpha', new BufferAttribute(this.alp, 1).setUsage(DynamicDrawUsage));
    g.setDrawRange(0, 0);
    const mat = new ShaderMaterial({
      uniforms: { map: { value: tex }, scale: { value: 400 } },
      vertexShader: PV,
      fragmentShader: PF,
      transparent: true,
      depthWrite: false,
      blending: additive ? AdditiveBlending : NormalBlending,
    });
    this.points = new Points(g, mat);
    this.points.frustumCulled = false;
  }

  get count(): number {
    return this.n;
  }

  /** Adds a particle unless the pool is full (the concept drops it too). */
  emit(p: ParticleSpec): void {
    if (this.n >= this.max) return;
    const s = this.state;
    const o = this.n * STRIDE;
    s[o + F.x] = p.x;
    s[o + F.y] = p.y;
    s[o + F.z] = p.z;
    s[o + F.vx] = p.vx;
    s[o + F.vy] = p.vy;
    s[o + F.vz] = p.vz;
    s[o + F.age] = 0;
    s[o + F.life] = p.life;
    s[o + F.drag] = p.drag || 0;
    s[o + F.grav] = p.grav || 0;
    s[o + F.s0] = p.s0;
    s[o + F.s1] = p.s1;
    // The concept's `p.a0 || 0.42` and `p.bright || 1` treat 0 as unset; NaN marks "unset" here.
    s[o + F.a0] = p.a0 ?? NaN;
    s[o + F.r] = p.r ?? 0;
    s[o + F.g] = p.g ?? 0;
    s[o + F.b] = p.b ?? 0;
    s[o + F.bright] = p.bright ?? NaN;
    s[o + F.floor] = p.floor ?? NaN;
    s[o + F.kind] = KIND[p.kind];
    this.n++;
  }

  /** Ages, moves and recolours every particle; drops dead ones keeping the survivors' order (concept L598). */
  update(dt: number): void {
    const s = this.state;
    let n = 0;
    for (let i = 0; i < this.n; i++) {
      const o = i * STRIDE;
      const age = (s[o + F.age] ?? 0) + dt;
      const life = s[o + F.life] ?? 0;
      if (age >= life) continue;
      const d = n * STRIDE;
      if (d !== o) s.copyWithin(d, o, o + STRIDE);
      s[d + F.age] = age;
      const dr = Math.exp(-(s[d + F.drag] ?? 0) * dt);
      const vx = (s[d + F.vx] ?? 0) * dr;
      let vy = (s[d + F.vy] ?? 0) * dr;
      const vz = (s[d + F.vz] ?? 0) * dr;
      vy += (s[d + F.grav] ?? 0) * dt;
      const x = (s[d + F.x] ?? 0) + vx * dt;
      let y = (s[d + F.y] ?? 0) + vy * dt;
      const z = (s[d + F.z] ?? 0) + vz * dt;
      const floor = s[d + F.floor] ?? NaN;
      if (!Number.isNaN(floor) && y < floor) {
        y = floor;
        vy = 0;
      }
      s[d + F.x] = x;
      s[d + F.y] = y;
      s[d + F.z] = z;
      s[d + F.vx] = vx;
      s[d + F.vy] = vy;
      s[d + F.vz] = vz;
      const k = age / life;
      const kind = s[d + F.kind];
      const a0 = s[d + F.a0] ?? NaN;
      let r: number;
      let g: number;
      let b: number;
      let a: number;
      if (kind === KIND.fire) {
        if (k < 0.1) {
          r = 1.5;
          g = lerp(1.0, 0.62, k / 0.1);
          b = lerp(0.45, 0.14, k / 0.1);
        } else if (k < 0.4) {
          const u = (k - 0.1) / 0.3;
          r = lerp(1.5, 0.95, u);
          g = lerp(0.62, 0.2, u);
          b = lerp(0.14, 0.04, u);
        } else if (k < 0.72) {
          const v = (k - 0.4) / 0.32;
          r = lerp(0.95, 0.3, v);
          g = lerp(0.2, 0.06, v);
          b = lerp(0.06, 0.03, v);
        } else {
          const w = (k - 0.72) / 0.28;
          r = lerp(0.5, 0.05, w);
          g = lerp(0.1, 0.02, w);
          b = 0.02;
        }
        a = (1 - sstep(0.45, 1, k)) * (a0 || 0.42);
      } else if (kind === KIND.smoke) {
        const lt = lerp(1, 1.35, k);
        r = (s[d + F.r] ?? 0) * lt;
        g = (s[d + F.g] ?? 0) * lt;
        b = (s[d + F.b] ?? 0) * lt;
        a = a0 * sstep(0, 0.08, k) * (1 - sstep(0.5, 1, k));
      } else if (kind === KIND.spark) {
        r = 3.5 * (1 - k * 0.6);
        g = 2.2 * (1 - k * 0.8);
        b = 0.8 * (1 - k);
        a = 1 - k;
      } else {
        const bright = s[d + F.bright] ?? NaN;
        r = g = b = bright || 1;
        a = a0 * (1 - sstep(0.3, 1, k));
      }
      const j = n * 3;
      this.pos[j] = x;
      this.pos[j + 1] = y;
      this.pos[j + 2] = z;
      this.col[j] = r;
      this.col[j + 1] = g;
      this.col[j + 2] = b;
      this.siz[n] = lerp(s[d + F.s0] ?? 0, s[d + F.s1] ?? 0, Math.sqrt(k));
      this.alp[n] = a;
      n++;
    }
    this.n = n;
    this.drawn = n;
    this.flush();
  }

  /** Moves every live particle by dz (the 800 m world shift). */
  shiftZ(dz: number): void {
    for (let i = 0; i < this.n; i++) this.state[i * STRIDE + F.z] = (this.state[i * STRIDE + F.z] ?? 0) + dz;
  }

  clear(): void {
    this.n = 0;
    this.drawn = 0;
    this.flush();
  }

  /** Draws recorded buffers instead of simulating (M1 state injection). */
  inject(buf: ParticleBuffer): void {
    const n = Math.min(buf.count, this.max);
    this.pos.set(buf.pos.slice(0, n * 3));
    this.col.set(buf.col.slice(0, n * 3));
    this.siz.set(buf.size.slice(0, n));
    this.alp.set(buf.alpha.slice(0, n));
    this.n = 0;
    this.drawn = n;
    this.flush();
  }

  /** Point-size scale for the drawing-buffer height and vertical FOV (concept L844). */
  setScale(drawingBufferHeight: number, fovDeg: number): void {
    const u = this.points.material.uniforms.scale;
    if (u) u.value = drawingBufferHeight / (2 * Math.tan((fovDeg * Math.PI) / 360));
  }

  /** Live particle data for tests (the concept's attribute arrays). */
  snapshot(): ParticleBuffer {
    const n = this.drawn;
    return {
      count: n,
      pos: Array.from(this.pos.subarray(0, n * 3)),
      col: Array.from(this.col.subarray(0, n * 3)),
      size: Array.from(this.siz.subarray(0, n)),
      alpha: Array.from(this.alp.subarray(0, n)),
    };
  }

  /** Particles in the attribute buffers: the live count after update(), or the injected count. */
  private drawn = 0;

  private flush(): void {
    const g = this.points.geometry;
    g.setDrawRange(0, this.drawn);
    for (const name of ATTRIBUTES) {
      const a = g.getAttribute(name);
      if (a) a.needsUpdate = true;
    }
  }
}

export type ParticleSystems = Record<'spray' | 'fire' | 'smoke' | 'sparks' | 'splash', Particles>;

/** The concept's five systems (concept L842–L843): jet-wash spray, fire, smoke, sparks and water splash. */
export function createParticleSystems(
  scene: { add(o: Points): unknown },
  dot: Texture | null,
  smoke: Texture | null,
): ParticleSystems {
  const sys: ParticleSystems = {
    spray: new Particles(700, smoke, false),
    fire: new Particles(900, dot, true),
    smoke: new Particles(700, smoke, false),
    sparks: new Particles(400, dot, true),
    splash: new Particles(900, smoke, false),
  };
  for (const p of [sys.spray, sys.smoke, sys.splash, sys.fire, sys.sparks]) scene.add(p.points);
  return sys;
}
